import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Tag, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TARIFS_IMG =
    "https://customer-assets.emergentagent.com/job_portail-pro/artifacts/dfx4qy3q_VIANDE%20QUALITE%202.jpg";
const SUGGESTIONS_IMG =
    "https://customer-assets.emergentagent.com/job_portail-pro/artifacts/0kkz9o80_DSCF0006.webp";

function LiquidTile({ to, label, title, description, image, testId }) {
    const ref = useRef(null);

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
    };

    return (
        <Link
            to={to}
            ref={ref}
            onMouseMove={onMove}
            className="liquid-tile group"
            data-testid={testId}
        >
            <div className="liquid-tile__media" style={{ backgroundImage: `url(${image})` }} />
            <div className="liquid-tile__scrim" />

            <div>
                <span className="liquid-chip">
                    {label === "Tarifs" ? <Tag size={12} strokeWidth={2} /> : <Sparkles size={12} strokeWidth={2} />}
                    {label}
                </span>
            </div>

            <div className="mt-8">
                <h2 className="font-serif text-[#F2ECE0] text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight">
                    {title}
                </h2>
                <p className="text-[#F2ECE0]/75 mt-3 text-sm leading-relaxed max-w-md">
                    {description}
                </p>
                <div className="mt-5 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4BC7C] font-medium">
                        Découvrir
                    </span>
                    <span className="liquid-arrow" aria-hidden>
                        <ArrowUpRight size={20} strokeWidth={1.6} />
                    </span>
                </div>
            </div>
        </Link>
    );
}

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // If the user is authenticated & approved, tiles go straight to protected pages.
    // Otherwise, we route to /connexion (ProtectedRoute will bring them back on success).
    const canAccess = user?.role === "admin" || user?.status === "approved";
    const tarifTarget = canAccess ? "/tarifs" : "/connexion";
    const sugTarget = canAccess ? "/suggestions" : "/connexion";

    return (
        <div className="liquid-bg" data-testid="landing-page">
            <section className="max-w-6xl mx-auto px-5 md:px-10 pt-6 md:pt-8 pb-6 md:pb-8 w-full">
                {/* Minimal header line */}
                <div className="flex items-center gap-3 mb-6 md:mb-8 bc-fade-in">
                    <div className="h-px w-10 bg-[#D4BC7C]/60" />
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4BC7C] font-medium">
                        Espace Pro
                    </p>
                </div>

                {/* Two liquid glass tiles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 bc-fade-in">
                    <LiquidTile
                        to={tarifTarget}
                        label="Tarifs"
                        title={<>Liste des prix<br /><span className="text-[#D4BC7C]">professionnels</span></>}
                        description="Nos tarifs HT en CHF, mis à jour en direct. Recherche, filtres par catégorie, export PDF."
                        image={TARIFS_IMG}
                        testId="tile-tarifs"
                    />
                    <LiquidTile
                        to={sugTarget}
                        label="Signature"
                        title={<>Suggestions<br /><span className="text-[#D4BC7C]">de la maison</span></>}
                        description="Le mot du boucher et nos créations de saison. Sélections signatures et pièces d'exception."
                        image={SUGGESTIONS_IMG}
                        testId="tile-suggestions"
                    />
                </div>

                {/* Access hint for anonymous visitors */}
                {!user && (
                    <div className="mt-6 flex flex-wrap items-center gap-4 justify-center bc-fade-in" data-testid="landing-access-hint">
                        <p className="text-xs text-[#A0A0A0]">
                            Accès réservé aux professionnels validés ·
                        </p>
                        <button
                            className="text-xs uppercase tracking-[0.18em] font-medium text-[#D4BC7C] hover:underline"
                            onClick={() => navigate("/connexion")}
                            data-testid="landing-login"
                        >
                            Se connecter
                        </button>
                        <span className="text-[#A0A0A0]/40">·</span>
                        <button
                            className="text-xs uppercase tracking-[0.18em] font-medium text-[#F2ECE0] hover:text-[#D4BC7C]"
                            onClick={() => navigate("/inscription")}
                            data-testid="landing-register"
                        >
                            Demander l&apos;accès
                        </button>
                    </div>
                )}
            </section>
        </div>
    );
}
